import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Card, CardBody, CardHeader, Button, Badge } from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";
import { useProfile } from "../../Components/Hooks/UserHooks";
import { useNavigate } from "react-router-dom";
import DeleteModal from "../../Components/Common/DeleteModal";
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteContract as onDeleteContract,
  getContracts as onGetContracts,
} from "../../slices/thunks";

const ContractTypeBadge = ({ type }) => {
  const typeColors = {
    Purchase: "primary",
    Sale: "success",
    Lease: "purple",
    Mortgage: "info",
    Other: "dark",
  };

  const color = typeColors[type] || "secondary";

  return (
    <Badge color={color} className={`badge-soft-${color}`}>
      {type}
    </Badge>
  );
};

const EmptyState = () => (
  <div className="text-center py-5">
    <div className="mb-3">
      <i className="ri-file-list-3-line display-4 text-muted"></i>
    </div>
    <h5 className="text-muted mb-2">No Contracts Found</h5>
    <p className="text-muted mb-3">
      No contracts available. Add your first contract to get started.
    </p>
    <Button
      color="primary"
      onClick={() => (window.location.href = "/add-contract")}
    >
      <i className="ri-add-line align-bottom me-1"></i> Add New Contract
    </Button>
  </div>
);

const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const selectContractState = (state) => state.Contract;
const selectContractProperties = createSelector(
  selectContractState,
  (contract) => ({
    contracts: contract.contracts,
    isContractSuccess: contract.isContractSuccess,
    error: contract.error,
    loading: contract.loading,
  })
);

const ContractTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { contracts, loading, error } = useSelector(selectContractProperties);
  const { userProfile } = useProfile();
  const contractRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tableData, setTableData] = useState([]);

  const isAdmin = userProfile?.user?.groups?.some((group) =>
    ["Admin", "Editor"].includes(group)
  );

  useEffect(() => {
    if (contracts?.length > 0) {
      setTableData(
        contracts.map((contract) => {
          const dateObj = new Date(contract.created_at || Date.now());
          return {
            ...contract,
            created_at_date: dateObj,
            formattedDate: dateObj.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
          };
        })
      );
    } else {
      setTableData([]);
    }
  }, [contracts]);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      await dispatch(onGetContracts());
    } catch (error) {
      console.error("Failed to fetch contracts:", error);
    }
  };

  const handleAddNewClick = useCallback(() => {
    navigate("/add-contract");
  }, [navigate]);

  const onClickDelete = useCallback((e, contract) => {
    e.stopPropagation();
    contractRef.current = contract;
    setDeleteModal(true);
  }, []);

  const handleDeleteContract = useCallback(async () => {
    const contract = contractRef.current;
    if (!contract || isDeleting) return;

    try {
      setIsDeleting(true);
      await dispatch(onDeleteContract(contract.id));
      setDeleteModal(false);
      await fetchContracts();
    } catch (error) {
      console.error("Error deleting contract:", error);
    } finally {
      setIsDeleting(false);
    }
  }, [dispatch, isDeleting]);

  const handleRowClick = useCallback(
    (row) => {
      navigate(`/contracts/${row.original.id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        header: "PVC Number",
        accessorKey: "pvc_number",
        enableColumnFilter: false,
        cell: ({ getValue }) => (
          <span className="fw-semibold">{getValue() || "—"}</span>
        ),
      },
      {
        header: "Notary",
        accessorKey: "notary",
        enableColumnFilter: false,
        cell: ({ getValue }) => getValue() || "—",
      },
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
        cell: ({ getValue }) => <ContractTypeBadge type={getValue()} />,
      },
      {
        header: "Notes",
        accessorKey: "notes",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const notes = getValue();
          if (!notes) return "—";
          return notes.length > 30 ? notes.substring(0, 30) + "..." : notes;
        },
      },
      {
        header: "Date",
        accessorKey: "created_at_date",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return row.original.formattedDate;
        },
        sortingFn: (rowA, rowB, columnId) => {
          const dateA = rowA.getValue(columnId);
          const dateB = rowB.getValue(columnId);
          
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          
          return dateA.getTime() - dateB.getTime();
        },
      },
      {
        id: "actions",
        enableColumnFilter: false,
        header: "",
        size: 60,
        cell: ({ row }) => {
          if (!isAdmin) return null;

          return (
            <div className="text-center">
              <ul className="list-inline d-flex justify-content-end align-items-center gap-2 mb-0">
                <li className="list-inline-item">
                  <button
                      to="#"
                      className="btn btn-sm btn-icon btn-ghost-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        onClickDelete(e, row.original);
                      }}
                  >
                    <i className="ri-delete-bin-5-fill fs-16"></i>
                  </button>
                </li>
              </ul>
            </div>
          );
        },
      },
    ],
    [isAdmin, onClickDelete, isDeleting]
  );

  // if (error) {
  //   return (
  //     <Card>
  //       <CardBody>
  //         <div className="text-center text-danger">
  //           <i className="ri-error-warning-line fs-24 mb-2"></i>
  //           <p>Failed to load contracts. Please try again later.</p>
  //           <Button color="primary" size="sm" onClick={fetchContracts}>
  //             Retry
  //           </Button>
  //         </div>
  //       </CardBody>
  //     </Card>
  //   );
  // }

  return (
    <Card id="contractList" className="shadow-sm">
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteContract}
        onCloseClick={() => setDeleteModal(false)}
        isDeleting={isDeleting}
      />

      <CardBody className="pt-0">
        {loading ? (
          <LoadingSpinner />
        ) : tableData?.length > 0 ? (
          <TableContainer
            columns={columns}
            data={tableData}
            isGlobalFilter={true}
            isAddclient={false}
            customPageSize={10}
            handleRowClick={handleRowClick}
            customTableRowProps={(row) => ({
              className: "cursor-pointer hover-row",
              style: { cursor: "pointer" },
            })}
            divClass="table-responsive table-card mt-3 mb-1"
            tableClass="align-middle table-nowrap"
            theadClass="table-light text-muted"
            SearchPlaceholder="Search contracts..."
          />
        ) : (
          <EmptyState />
        )}
      </CardBody>
    </Card>
  );
};

export default ContractTable;
